import React, { useEffect, useState } from 'react'
import { Grid, Box } from '@material-ui/core'
import { useFormik, FormikProvider } from 'formik'
import Container from 'presentation/hospital/components/Container'
import Button from 'presentation/shared/components/Button'
import ActualPage from 'presentation/shared/components/ActualPage'
import { EmergencyRoomCardDetails } from 'presentation/hospital/components/EmergencyRoomDetails/EmergencyRoomCardDetails'
import { EmergencyRoomAttendanceModel } from 'domain/entities/emergency-room-attendance.model'
import {
  EmergencyRoomEditFormikValues,
  emergencyRoomValidationSchema
} from './types'
import { PatientDetails } from 'presentation/hospital/components/EmergencyRoomDetails/Forms/PatientDetails'
import { PatientSymptoms } from 'presentation/hospital/components/EmergencyRoomDetails/Forms/PatientSymptoms'
import { PatientAccompanying } from 'presentation/hospital/components/EmergencyRoomDetails/Forms/PatientAccompanying'
import moment from 'moment-timezone'
import { cpfMask, phoneMask } from 'presentation/utils/masks'
import { conformToMask } from 'react-text-mask'
import { EmergencyRoomDefaultDoctor } from 'presentation/hospital/components/EmergencyRoomDetails/EmergencyRoomDefaultDoctor'
import { useServices } from 'presentation/hooks/use-services'
import TypedModal from 'presentation/shared/components/Modal'
import SupportText from 'presentation/shared/components/SupportText'
import { useHistory } from 'react-router'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { toast } from 'react-toastify'
import { ReligionModel } from 'domain/entities/religion-model'

type EmergencyRoomDetailsLayoutProps = {
  emergencyRoom?: EmergencyRoomAttendanceModel
  religions: ReligionModel[]
  refetchEmergencyRoom: () => Promise<void>
} & WithLoadingProps

function EmergencyRoomDetailsLayout({
  emergencyRoom,
  setIsLoading,
  refetchEmergencyRoom,
  religions
}: EmergencyRoomDetailsLayoutProps) {
  const [newEmergencyId, setNewEmergencyId] = useState<number>()
  const [errorString, setErrorString] = useState<string>()
  const history = useHistory()
  const emergencyRoomService = useServices().emergencyRoom
  const patientService = useServices().patient
  const accompanyingService = useServices().accompanying
  const formik = useFormik<EmergencyRoomEditFormikValues>({
    initialValues: {
      isEditing: false
    } as EmergencyRoomEditFormikValues,
    validationSchema: emergencyRoomValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true)
        const response = await emergencyRoomService.createEmergencyRoomInMV({
          patient_id: values.updatePatient.patient_id,
          doctor_id: values.doctor?.doctor_id
        })
        if (response.emergency_room_attendance_id) {
          return setNewEmergencyId(response.emergency_room_attendance_id)
        }
        return setErrorString('Erro')
      } catch (error: any) {
        setErrorString(String(error.message))
      } finally {
        setIsLoading(false)
      }
    }
  })

  const { isEditing } = formik.values

  const handleEditPatient = async () => {
    try {
      const { updatePatient } = formik.values
      delete updatePatient.cpf
      await patientService.updatePatientById({
        ...updatePatient,
        phone: updatePatient.phone?.replace(/\D+/g, ''),
        birthday: moment(updatePatient.birthday, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )
      })
    } catch (error: any) {
      toast.error(error.message)
    }
  }

  const handleEditAccompanying = async () => {
    try {
      if (!formik.values.updateAccompanying) return
      await accompanyingService.updateAccompanying(
        formik.values.updateAccompanying
      )
    } catch (error: any) {
      toast.error(error.message)
    }
  }

  const handleEdit = async () => {
    setIsLoading(true)
    await handleEditPatient()
    await handleEditAccompanying()
    formik.setFieldValue('isEditing', false)
    setIsLoading(false)
    await refetchEmergencyRoom()
  }

  const handleSubmit = () => {
    if (isEditing) {
      handleEdit()
    } else {
      formik.handleSubmit()
    }
  }

  const getInitialPatient = () => {
    if (!emergencyRoom?.patient) return
    return {
      ...emergencyRoom?.patient,
      birthday:
        moment(emergencyRoom?.patient?.birthday).format('DD/MM/YYYY') || '',
      cpf:
        conformToMask(emergencyRoom?.patient?.cpf, cpfMask)?.conformedValue ||
        '',
      phone: conformToMask(emergencyRoom?.patient?.phone || '', phoneMask)
        ?.conformedValue,
      name: emergencyRoom?.patient?.name || '',
      gender: emergencyRoom?.patient?.gender || '',
      email: emergencyRoom?.patient?.email || '',
      maritalStatus: emergencyRoom?.patient?.maritalStatus || ''
    }
  }

  const getInitialAccompanying = () => {
    if (!emergencyRoom?.accompanies?.[0]) return
    return emergencyRoom.accompanies[0].toJSON()
  }

  const setData = (data: Partial<EmergencyRoomEditFormikValues>) => {
    formik.setValues({ ...formik.values, ...data }, true)
  }

  useEffect(() => {
    const updatePatient = getInitialPatient()
    const updateAccompanying = getInitialAccompanying()
    setData({
      updatePatient,
      updateAccompanying
    })
  }, [emergencyRoom])

  return (
    <Container hideButton disableScroll>
      <FormikProvider value={formik}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ActualPage text="Voltar" />
          </Grid>
          <Grid container item xs={12}>
            <EmergencyRoomCardDetails emergencyRoom={emergencyRoom} />
          </Grid>
          <Grid container item xs={12}>
            <EmergencyRoomDefaultDoctor />
          </Grid>
          <Grid container item xs={12}>
            <Box mt={4} mb={4}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <PatientDetails religions={religions} />
                </Grid>
                <Grid item xs={12}>
                  <PatientSymptoms emergencyRoom={emergencyRoom} />
                </Grid>
                <Grid item xs={12}>
                  <PatientAccompanying />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid container item xs={12} justifyContent="flex-end">
            <Button onClick={handleSubmit} disabled={!formik.isValid}>
              {isEditing ? 'Salvar' : 'Gerar atendimento no MV'}
            </Button>
          </Grid>
        </Grid>
      </FormikProvider>
      <TypedModal
        type="check"
        show={!!newEmergencyId}
        actionLabel="Voltar a tela inicial"
        action={() => history.push('/home')}
        close={() => history.push('/home')}
        title="Atendimento iniciado no MV com sucesso"
        description={
          <>
            <SupportText>Número do atendimento</SupportText>
            <strong>{newEmergencyId}</strong>
          </>
        }
        style={{ width: '400px' }}
        preventAutomateClose
      />
      <TypedModal
        type="alert"
        show={!!errorString}
        actionLabel="Voltar para mais detalhes"
        action={() => setErrorString(undefined)}
        close={() => setErrorString(undefined)}
        title={errorString}
        description={'Esse atendimento não poderá ser iniciado'}
        style={{ width: '400px' }}
        preventAutomateClose
      />
    </Container>
  )
}

export default WithLoading(EmergencyRoomDetailsLayout)
